import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils.js'
import { motion } from 'framer-motion'

export const GradientAnimationIcon = ({ icon, className, gradientColors = ["#f08080", "#42A5F5", "#f08080"], animationDuration = "6s", size = 24 }) => {
    const [svgContent, setSvgContent] = useState(null)
    const iconRef = useRef(null)

    useEffect(() => {
        // Function to extract SVG content from Iconify icon
        const extractSvgContent = () => {
            if (iconRef.current) {
                // Get the SVG element that Iconify creates
                const svgElement = iconRef.current.querySelector("svg")
                if (svgElement) {
                    // Get the SVG as a string
                    const svgString = svgElement.outerHTML
                    setSvgContent(svgString)
                }
            }
        }

        // Wait a bit for Iconify to render the icon
        const timer = setTimeout(extractSvgContent, 100)
        return () => clearTimeout(timer)
    }, [icon])

    // Create CSS variable values for the gradient
    const gradientValue = `linear-gradient(90deg, ${gradientColors.join(", ")})`

    return (
        <div className={cn("inline-block relative", className)}>
            {/* Hidden icon to extract SVG content */}
            <div ref={iconRef} className="absolute opacity-0 pointer-events-none">
                {icon}
            </div>

            {/* Visible gradient icon */}
            {svgContent && (
                <div
                    className="animate-gradient bg-[length:300%_100%]"
                    style={{
                        width: typeof size === "number" ? `${size}px` : size,
                        height: typeof size === "number" ? `${size}px` : size,
                        maskImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(svgContent)}")`,
                        WebkitMaskImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(svgContent)}")`,
                        maskSize: "contain",
                        WebkitMaskSize: "contain",
                        maskRepeat: "no-repeat",
                        WebkitMaskRepeat: "no-repeat",
                        maskPosition: "center",
                        WebkitMaskPosition: "center",
                        backgroundImage: gradientValue,
                        animationDuration: animationDuration,
                    }}
                />
            )}
        </div>
    )
}



export const GradientAnimationText = ({ children, className, gradientColors = ["#f08080", "#42A5F5", "#f08080"], animationDuration = "3s" }) => {
    const gradientValue = `linear-gradient(90deg, ${gradientColors.join(", ")})`
    return (
        <div className={cn("inline-block relative", className)}>
            <div
                className="bg-gradient-to-r from-transparent to-transparent bg-[length:300%_100%] bg-clip-text text-transparent animate-gradient"
                style={{
                    backgroundImage: gradientValue,
                    animationDuration: animationDuration,
                }}
            >
                {children}
            </div>
        </div>
    )
}


// "background" or "outline"
// "sm", "md", "lg"
export const GradientButton = ({ children, className, variant = "background", gradientColors = ["#f08080", "#42A5F5", "#f08080"], animationDuration = "6s", size = "md", ...props }) => {
    // Size classes
    const sizeClasses = {
        sm: "px-3 py-1.5 text-sm",
        md: "px-4 py-2 text-base",
        lg: "px-6 py-2.5 text-lg",
    }

    // Common button classes
    const buttonClasses = cn(
        "relative inline-flex items-center justify-center rounded-md font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none",
        sizeClasses[size],
        className,
    )

    // For gradient background variant
    if (variant === "background") {
        return (
            <button
                className={cn(buttonClasses, "text-white overflow-hidden")}
                style={{
                    "--button-gradient": `linear-gradient(90deg, ${gradientColors.join(", ")})`,
                }}
                {...props}
            >
                <div className="relative z-10 flex items-center gap-1">{children}</div>
                <span
                    className="absolute inset-0 animate-gradient bg-[length:300%_100%]"
                    style={{
                        backgroundImage: `linear-gradient(90deg, ${gradientColors.join(", ")})`,
                        animationDuration: animationDuration,
                    }}
                />
            </button>
        )
    }

    // For gradient outline and text variant
    if (variant === "outline") {
        return (
            <button
                className={cn(buttonClasses, "border-2 bg-transparent relative overflow-hidden flex items-center gap-1 rounded")}
                style={{
                    borderImageSlice: 1,
                    borderImageSource: `linear-gradient(90deg, ${gradientColors.join(", ")})`,
                }}
                {...props}
            >
                {/* Animated border using pseudo-element */}
                <span
                    className="absolute inset-[-2px] animate-gradient rounded-md"
                    style={{
                        backgroundImage: `linear-gradient(90deg, ${gradientColors.join(", ")})`,
                        animationDuration: animationDuration,
                        WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        maskComposite: "exclude",
                        maskClip: "border-box",
                    }}
                />

                {/* Gradient text */}
                <GradientAnimationText gradientColors={gradientColors} animationDuration={animationDuration}>
                    {children}
                </GradientAnimationText>
            </button>
        )
    }

    // Default fallback
    return (
        <button className={buttonClasses} {...props}>
            {children}
        </button>
    )
}



export const BorderBeam = ({
    className,
    size = 50,
    delay = 0,
    duration = 6,
    colorFrom = "#ffaa40",
    colorTo = "#9c40ff",
    transition,
    style,
    reverse = false,
    initialOffset = 0, }) => {

    return (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] border border-transparent [mask-clip:padding-box,border-box] [mask-composite:intersect] [mask-image:linear-gradient(transparent,transparent),linear-gradient(#000,#000)]">
            <motion.div
                className={cn(
                    "absolute aspect-square",
                    "bg-gradient-to-l from-[var(--color-from)] via-[var(--color-to)] to-transparent",
                    className,
                )}
                style={
                    {
                        width: size,
                        offsetPath: `rect(0 auto auto 0 round ${size}px)`,
                        "--color-from": colorFrom,
                        "--color-to": colorTo,
                        ...style,
                    }
                }
                initial={{ offsetDistance: `${initialOffset}%` }}
                animate={{
                    offsetDistance: reverse
                        ? [`${100 - initialOffset}%`, `${-initialOffset}%`]
                        : [`${initialOffset}%`, `${100 + initialOffset}%`],
                }}
                transition={{
                    repeat: Infinity,
                    ease: "linear",
                    duration,
                    delay: -delay,
                    ...transition,
                }}
            />
        </div>
    );
};


export const GradientText = ({ children, className = "", colors = ["#f08080", "#42A5F5", "#f08080"], animationSpeed = 8, showBorder = false, showFill }) => {

    const gradientStyle = {
        backgroundImage: `linear-gradient(to right, ${colors.join(", ")})`,
        animationDuration: `${animationSpeed}s`,
    };

    if (showFill) {
        return (
            <div
                className={`inline-block relative z-2 text-transparent bg-cover animate-gradient ${className}`}
                style={{
                    ...gradientStyle,
                    backgroundSize: "300% 100%",
                }}
            >
                {/*<div className="bg-white">*/}
                {children}
                {/*</div>*/}
            </div>
        )
    }

    return (
        <div
            className={`relative mx-auto flex max-w-fit flex-row items-center justify-center rounded-lg font-medium backdrop-blur transition-shadow duration-500 overflow-hidden ${className}`}
        >
            {showBorder && (
                <div
                    className="absolute inset-0 bg-cover z-0 pointer-events-none animate-gradient"
                    style={{
                        ...gradientStyle,
                        backgroundSize: "300% 100%",
                    }}
                >
                    <div
                        className="absolute inset-0 bg-white rounded-lg z-[-1]"
                        style={{
                            width: "calc(100% - 3px)",
                            height: "calc(100% - 3px)",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                        }}
                    ></div>
                </div>
            )}
            <div
                className="inline-block relative z-2 text-transparent bg-cover animate-gradient"
                style={{
                    ...gradientStyle,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    backgroundSize: "300% 100%",
                }}
            >
                {children}
            </div>
        </div>
    )
}

export const GradientTextWithBorder = ({ children, className = "", colors = ["#f08080", "#42A5F5", "#f08080"], animationSpeed = 8, showBorder = false, showFill }) => {

    const gradientStyle = {
        backgroundImage: `linear-gradient(to right, ${colors.join(", ")})`,
        animationDuration: `${animationSpeed}s`,
    };

    if (showFill) {
        return (
            <div
                className={`inline-block relative z-2 text-transparent bg-cover animate-gradient ${className} rounded`}
                style={{
                    ...gradientStyle,
                    backgroundSize: "300% 100%",
                }}
            >
                <div className="text-primary-foreground">
                    {children}
                </div>
            </div>
        )
    }

    return (
        <div
            className={`relative mx-auto flex max-w-fit flex-row items-center justify-center rounded-lg font-medium backdrop-blur transition-shadow duration-500 overflow-hidden ${className}`}
        >
            {showBorder && (
                <div
                    className="absolute inset-0 bg-cover z-0 pointer-events-none animate-gradient"
                    style={{
                        ...gradientStyle,
                        backgroundSize: "300% 100%",
                    }}
                >
                    <div
                        className="absolute inset-0 bg-white rounded-lg z-[-1]"
                        style={{
                            width: "calc(100% - 3px)",
                            height: "calc(100% - 3px)",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                        }}
                    ></div>
                </div>
            )}
            <div
                className="inline-block relative z-2 text-transparent bg-cover animate-gradient"
                style={{
                    ...gradientStyle,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    backgroundSize: "300% 100%",
                }}
            >
                {children}
            </div>
        </div>
    )
}

// tailwind.config.js
// module.exports = {
//   theme: {
//     extend: {
//       keyframes: {
//         gradient: {
//           '0%': { backgroundPosition: '0% 50%' },
//           '50%': { backgroundPosition: '100% 50%' },
//           '100%': { backgroundPosition: '0% 50%' },
//         },
//       },
//       animation: {
//         gradient: 'gradient 8s linear infinite'
//       },
//     },
//   },
//   plugins: [],
// };
